import { Alert, LinearProgress, Link } from '@mui/material';
import { Box, Stack, Typography } from './mui-system';
import { api } from '../api/client';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import type { TaskResourceProfile } from '../../../api/src/queue/task-resource-profile';

const sourceNames: Record<TaskResourceProfile['source'], string> = {
  queue: 'Общая очередь',
  donors: 'Проверки доноров',
  materializer: 'Материализация',
};
const number = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
const duration = (ms: number | null) =>
  ms === null ? '—' : ms < 1000 ? `${number(ms)} мс` : `${number(ms / 1000)} с`;
const memory = (bytes: number | null) =>
  bytes === null ? '—' : `${number(bytes / 1024 / 1024)} МиБ`;

export function TaskResourceProfiles() {
  const { data, error } = useVisiblePolling(api.jobResourceProfiles);
  return (
    <Stack gap={2} sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="h6">Потребление по типам задач</Typography>
      <Typography color="text.secondary">
        Очередь и доноры: замеры всего процесса, включая другую работу. Материализация: отдельный
        процесс. Параллелизм автоматически не меняется.
      </Typography>
      {error && (
        <Alert severity="warning">
          Не удалось обновить профили: {error}. {data ? 'Ниже последние полученные данные.' : ''}
        </Alert>
      )}
      {!data && !error && <LinearProgress aria-label="Загрузка профилей ресурсов" />}
      {data && (
        <>
          <Typography color="text.secondary">
            P95 — значение, выше которого не более 5% пригодных замеров. Ошибки и пересечения
            исключены. История: до {data.limits.perKind} вызовов на тип за {data.limits.days} дней.
          </Typography>
          {!data.items.length && (
            <Alert severity="info">Замеров пока нет. Они появятся после выполнения задач.</Alert>
          )}
          {data.items.map((profile) => (
            <Box
              key={JSON.stringify([
                profile.source,
                profile.kind,
                profile.scope,
                profile.capacity,
                profile.runtime,
              ])}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflowWrap: 'anywhere',
              }}
            >
              <Stack gap={1}>
                <Typography fontWeight={700}>
                  {sourceNames[profile.source]} · {profile.kind}
                </Typography>
                <Typography color="text.secondary">
                  {profile.scope === 'dedicated-process'
                    ? 'Отдельный процесс'
                    : 'Наблюдение за общим процессом'}
                  .{' '}
                  {profile.capacity
                    ? `Бюджет: ${number(profile.capacity.cpu)} CPU / ${memory(profile.capacity.memoryBytes)}.`
                    : 'Бюджет среды неизвестен.'}{' '}
                  {profile.runtime}
                </Typography>
                <Typography>
                  Пригодных замеров: {profile.usable} из {profile.total}.
                  {profile.usable < data.limits.minimumSamples
                    ? ` Мало данных: нужно хотя бы ${data.limits.minimumSamples}.`
                    : ''}
                </Typography>
                <Typography>
                  P95: длительность {duration(profile.p95.durationMs)} · CPU{' '}
                  {duration(profile.p95.cpuMs)} · пик памяти {memory(profile.p95.peakRssBytes)}
                </Typography>
                {profile.scope === 'process-window' && (
                  <Typography>Прирост памяти P95: {memory(profile.p95.rssGrowthBytes)}</Typography>
                )}
                <Typography color="text.secondary">
                  Ошибки обработчика: {profile.failed} · пересечения: {profile.overlapped} · без
                  замера: {profile.unmeasured}. Последний:{' '}
                  {new Date(profile.lastAt).toLocaleString('ru-RU')}.
                </Typography>
              </Stack>
            </Box>
          ))}
          <Typography color="text.secondary">
            История выборочная, короткие пики памяти могут быть пропущены. Возврат обработчика не
            означает успех всех вложенных операций.{' '}
            <Link
              href="/docs/background-workers#наблюдение-за-потреблением-задач"
              target="_blank"
              rel="noopener"
            >
              Методика и ограничения
            </Link>
          </Typography>
        </>
      )}
    </Stack>
  );
}
